import React, { useState, useEffect } from 'react';
import { clearAllCookies, clearCookie, getCookie, setCookie } from '../helpers/common';
import { createContextHook } from 'use-context-hook';
import Toast from '../components/molecules/Toast';
import authService from '../services/authService';
import teamService from '../services/teamService';
import { useCancellablePromise } from '../helpers/promiseHandler';
import { disconnectSocket } from '../helpers/socketConnection/socketConnection';
import { getReferralCode } from '../helpers/referralStorage';

const context = {};

export const AuthContext = createContextHook(context);

export function AuthContextProvider(props) {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [loading_user, setLoadingUser] = useState(false);
  const [loading_user_google, setLoadingUserGoogle] = useState(false);
  const [fetch_user, setFetchUser] = useState(false);
  const { cancellablePromise } = useCancellablePromise();
  const [reFetch, setRefetch] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [socketData, setSocketData] = useState(null);

  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [allowedPages, setAllowedPages] = useState([]);

  // Consider user logged in if they have a valid token, regardless of status
  const [isLoggedIn, setIsLoggedIn] = useState(!!getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE));

  const onLogout = async () => {
    function handleCookie(res) {
      if (res) {
        clearAllCookies();
      }
    }
    try {
      if (isLoggedIn) setLoadingUser(true);
      const res = await authService.removeAdminJwt();
      handleCookie(res);
    } catch (ex) {
      clearAllCookies();
      Toast({ type: 'error', message: ex?.message });
    } finally {
      disconnectSocket();

      // Clear all cookies explicitly
      clearCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE);
      clearCookie(process.env.REACT_APP_AI_STATUS_COOKIE);

      setLoadingUser(false);
      setIsLoggedIn(false);
      setUser({});
      setWorkspaces([]);
      setSelectedWorkspace(null);
      setPermissions([]);
      setAllowedPages([]);
      setAuthReady(true);
      setAuthError(false);
    }
  };

  const getPermissions = async (shouldUpdate = false) => {
    if (!shouldUpdate) {
      setLoadingUser(true);
    }
    try {
      const res = await cancellablePromise(authService.getCurrentAdmin());
      setUser(res);
      setIsLoggedIn(true);
      if (res.data.tour_completed) {
        localStorage.setItem('hasSeenTour', 'true');
      }
    } catch (err) {
      setIsLoggedIn(false);
      setAuthError(true);
      Toast({ type: 'error', message: err.message });
    } finally {
      setLoadingUser(false);
      setAuthReady(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      getPermissions();
    } else {
      setAuthReady(true);
    }
    // listen to event
    window.addEventListener('FETCH_ADMIN_ROLE', () => {
      getPermissions();
    });
    return () => {
      window.removeEventListener('FETCH_ADMIN_ROLE', () => {
        getPermissions();
      });
    };
  }, [isLoggedIn, fetch_user, reFetch]);

  const UserPermissions = () => {
    setLoadingUser(true);
    cancellablePromise(teamService.getCurrentUserWorkspace())
      .then(res => {
        const workspaceItems = res?.data?.items || [];
        setWorkspaces(workspaceItems);

        // Always update permissions when workspace data is fetched
        if (workspaceItems.length === 1) {
          const firstWorkspace = workspaceItems[0];
          setSelectedWorkspace(firstWorkspace);
          updateUserPermissions(firstWorkspace);
        } else if (workspaceItems.length > 1) {
          // For multiple workspaces, select the first one if none is selected
          if (!selectedWorkspace) {
            const firstWorkspace = workspaceItems[0];
            setSelectedWorkspace(firstWorkspace);
            updateUserPermissions(firstWorkspace);
          } else {
            // Update permissions for currently selected workspace
            const currentWorkspace = workspaceItems.find(w => w.id === selectedWorkspace.id);
            if (currentWorkspace) {
              updateUserPermissions(currentWorkspace);
            } else {
              // If selected workspace no longer exists, select the first one
              const firstWorkspace = workspaceItems[0];
              setSelectedWorkspace(firstWorkspace);
              updateUserPermissions(firstWorkspace);
            }
          }
        }

        setLoadingUser(false);
      })
      .catch(err => {
        setAllowedPages(['no-permissions']);
        setLoadingUser(false);
        Toast({
          type: 'error',
          message: err.message,
        });
      });
  };

  const updateUserPermissions = workspace => {
    if (workspace && workspace.permissionDetails) {
      const permissionDetails = workspace.permissionDetails || [];

      const navRoutes = permissionDetails
        .filter(perm => perm.can.endsWith('.nav'))
        .map(perm => perm.route)
        .filter(Boolean);

      const allRoutes = permissionDetails.map(perm => perm.route).filter(Boolean);

      setAllowedPages(allRoutes);
      setCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE, JSON.stringify(allRoutes));

      const canPermission = (permissionDetails || []).map(p => p.can);
      setPermissions(canPermission);

      setUser(prevUser => {
        const userData = prevUser.data || prevUser;

        const updatedData = {
          ...userData,
          permissions: canPermission,
          //TODO: uncommit it in case of need
          // workspaceRole: workspace.myRole,
          workspacePermissionDetails: permissionDetails,
          allowedPages: allRoutes,
          navRoutes,
        };

        if (prevUser.data) {
          return {
            ...prevUser,
            data: updatedData,
          };
        }

        return updatedData;
      });
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      getPermissions();
      UserPermissions();
    } else {
      setAuthReady(true);
    }

    window.addEventListener('FETCH_ADMIN_ROLE', () => {
      getPermissions();
    });
    return () => {
      window.removeEventListener('FETCH_ADMIN_ROLE', () => {
        getPermissions();
      });
    };
  }, [isLoggedIn, fetch_user, reFetch]);

  const onLogin = async ({ email, password }, navigate) => {
    setLoadingUser(true);
    try {
      // Clear previous user data and cookies before login
      clearCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE);

      setUser({});
      setWorkspaces([]);
      setSelectedWorkspace(null);
      setPermissions([]);
      setAllowedPages([]);

      const res = await authService.login({
        email,
        password,
      });
      if (!res?.data?.token) {
        throw new Error(res?.message);
      }

      const token = res?.data?.token;
      const userData = res?.data?.user;

      setCookie(process.env.REACT_APP_AI_TOKEN_COOKIE, token);
      setCookie(process.env.REACT_APP_AI_STATUS_COOKIE, userData?.status);
      setUser(userData);
      setIsLoggedIn(true);

      if (!userData?.isEmailVerified) {
        localStorage.setItem('email', email);
      }
    } catch ({ message }) {
      setIsLoggedIn(false);
      Toast({ type: 'error', message });
    } finally {
      setLoadingUser(false);
    }
  };

  const onGoogleLogin = async ({ access_token, type, sellerType, action }) => {
    setLoadingUserGoogle(true);
    setLoading(true);
    try {
      // Clear previous user data and cookies before login
      clearCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE);

      setUser({});
      setWorkspaces([]);
      setSelectedWorkspace(null);
      setPermissions([]);
      setAllowedPages([]);

      const referral_code = getReferralCode() || getCookie('referral_code');

      const res = await authService.googleLogin({
        access_token,
        action,
        referral_code,
      });

      if (!res?.data?.token) {
        throw new Error(res?.message);
      }

      const token = res?.data?.token;
      const userData = res?.data;

      // Affiliate tracking for Google sign up
      if (action === 'Register' && window?.promotekit?.refer) {
        try {
          const email = userData?.email || userData?.user?.email;
          const stripeCustomerId = userData?.stripeCustomerId || userData?.user?.stripeCustomerId;

          if (email) {
            if (stripeCustomerId) {
              window.promotekit.refer(email, stripeCustomerId);
            } else {
              window.promotekit.refer(email);
            }
          }
        } catch (trackErr) {
          // eslint-disable-next-line no-console
          console.log('PromoteKit refer tracking failed:', trackErr?.message || trackErr);
        }
      }

      setCookie(process.env.REACT_APP_AI_TOKEN_COOKIE, token);
      setCookie(process.env.REACT_APP_AI_STATUS_COOKIE, userData?.status);
      setUser(userData);
      setIsLoggedIn(true);
    } catch ({ message }) {
      setIsLoggedIn(false);
      setLoadingUserGoogle(false);
      Toast({ type: 'error', message });
    } finally {
      setLoadingUserGoogle(false);
      setLoading(false);
    }
  };

  const hasPermission = perm => {
    const perms =
      Array.isArray(permissions) && permissions.length > 0
        ? permissions
        : user?.permissions || user?.data?.permissions || [];
    const result = Array.isArray(perms) && perms.includes(perm);
    return result;
  };

  return (
    <AuthContext.Provider
      value={{
        setIsLoggedIn,
        onLogout,
        onLogin,
        refetch: () => setRefetch(_ => !_),
        fetchUser: () => setFetchUser(() => !fetch_user),
        setLoading,
        hasPermission,
        loading,
        isLoggedIn,
        fetch: reFetch,
        user,
        loading_user,
        onGoogleLogin,
        loading_user_google,
        getPermissions,
        authReady,
        authError,
        setSocketData,

        UserPermissions,
        allowedPages,
        setAllowedPages,
        workspaces,
        setWorkspaces,
        selectedWorkspace,
        setSelectedWorkspace,
        updateUserPermissions,
        permissions,
      }}>
      {props.children}
    </AuthContext.Provider>
  );
}
